import React, { useEffect, useState } from 'react';
import { useInboxSendJobs } from '../contexts/InboxSendJobsContext';
import './InboxSendingBar.css';

/**
 * InboxSendingBar — fixed-position corner stack of active/recent send jobs.
 *
 * Mounted once at the app root (App.js) so it survives every route change.
 * Renders one card per job in `InboxSendJobsContext`:
 *   - status="sending" → animated progress bar based on expectedDurationMs
 *   - status="success" → filled green bar, auto-dismisses (ttl handled by ctx)
 *   - status="error"   → red bar with dismiss button; stays until dismissed
 *
 * The bar is intentionally lightweight — it doesn't own send logic and can't
 * cancel a job (there is no server-side cancel primitive for a live HTTP
 * `sendWorldEmail` request).
 */
export default function InboxSendingBar() {
  const { jobs, dismissJob } = useInboxSendJobs();

  if (!jobs || jobs.length === 0) return null;

  return (
    <div className="ivx-sendbar-stack" role="status" aria-live="polite">
      {jobs.map((job) => (
        <SendJobCard key={job.id} job={job} onDismiss={() => dismissJob(job.id)} />
      ))}
    </div>
  );
}

function SendJobCard({ job, onDismiss }) {
  // Tick every 250ms while sending so the progress bar animates smoothly.
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (job.status !== 'sending') return undefined;
    const iv = setInterval(() => forceTick((n) => n + 1), 250);
    return () => clearInterval(iv);
  }, [job.status]);

  const label = job.kind === 'forward' ? 'Forwarding' : 'Sending';
  const doneLabel = job.kind === 'forward' ? 'Forwarded' : 'Sent';

  let progress; // 0..1
  if (job.status === 'sending') {
    const elapsed = Date.now() - job.startedAt;
    const expected = Math.max(1000, job.expectedDurationMs || 12000);
    // Ease toward 95% while waiting so the bar never sits at 100% pre-response.
    const raw = elapsed / expected;
    progress = Math.min(0.95, raw * 0.95);
  } else {
    progress = 1;
  }

  const recipientLine = job.recipient || 'recipient';
  const subjectLine = job.subject || '';

  return (
    <div className={`ivx-sendbar-card ivx-sendbar-${job.status}`}>
      <div className="ivx-sendbar-head">
        <div className="ivx-sendbar-title">
          {job.status === 'sending' && (
            <>
              <span className="ivx-sendbar-spinner" aria-hidden="true" />
              <span>{label} to {recipientLine}…</span>
            </>
          )}
          {job.status === 'success' && (
            <>
              <SuccessIcon />
              <span>{doneLabel} to {recipientLine}</span>
            </>
          )}
          {job.status === 'error' && (
            <>
              <ErrorIcon />
              <span>Could not send to {recipientLine}</span>
            </>
          )}
        </div>
        {(job.status === 'error' || job.status === 'success') && (
          <button
            type="button"
            className="ivx-sendbar-close"
            onClick={onDismiss}
            aria-label="Dismiss"
          >
            ×
          </button>
        )}
      </div>

      {subjectLine && (
        <div className="ivx-sendbar-subject" title={subjectLine}>{subjectLine}</div>
      )}

      {job.status === 'error' && job.message && (
        <div className="ivx-sendbar-error-msg">{job.message}</div>
      )}

      <div className="ivx-sendbar-track" aria-hidden="true">
        <div
          className="ivx-sendbar-fill"
          style={{ width: `${Math.round(progress * 100)}%` }}
        />
      </div>
    </div>
  );
}

function SuccessIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M5 12.5l4.5 4.5L19 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ErrorIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 8v5m0 3.5v.1" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}
